import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient, withClientId } from '@/lib/queryClient'

import {
  createConsignment,
  deleteConsignment,
  fetchConsignmentByBoat,
  fetchConsignmentById,
  fetchConsignments,
  recordPayout,
  recordSale,
  updateConsignment,
  withdrawConsignment,
  type ConsignmentFilters,
} from './api'
import type {
  Consignment,
  ConsignmentTermsBody,
  ConsignmentWriteBody,
  RecordSaleBody,
} from '@/types/consignment'

export const CONSIGNMENTS_KEY = ['consignments'] as const

export const useConsignments = (filters: ConsignmentFilters = {}) => {
  return useQuery({
    queryKey: ['consignments', { ...filters }],
    queryFn: () => fetchConsignments(filters),
  })
}

export const useConsignment = (id: string) => {
  return useQuery({
    queryKey: ['consignments', id],
    queryFn: () => fetchConsignmentById(id),
    enabled: !!id,
  })
}

// The open agreement for a boat (null = none) — powers the boat detail card.
export const useConsignmentByBoat = (boatId: string | undefined) => {
  return useQuery({
    queryKey: ['consignments', 'by-boat', boatId],
    queryFn: () => fetchConsignmentByBoat(boatId!),
    enabled: !!boatId,
  })
}

// Every mutation touches both the consignments cache and the boats cache:
// creating/withdrawing changes the boat's embedded consignment brief, and
// recording a sale flips the boat's listing state to sold.
const useInvalidateConsignmentsAndBoats = () => {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['consignments'] })
    qc.invalidateQueries({ queryKey: ['boats'] })
  }
}

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below.
export const CONSIGNMENT_CREATE_KEY = ['outbox', 'consignments', 'create'] as const
export const CONSIGNMENT_UPDATE_KEY = ['outbox', 'consignments', 'update'] as const
export const CONSIGNMENT_DELETE_KEY = ['outbox', 'consignments', 'delete'] as const
export const CONSIGNMENT_SALE_KEY = ['outbox', 'consignments', 'record-sale'] as const
export const CONSIGNMENT_PAYOUT_KEY = ['outbox', 'consignments', 'record-payout'] as const
export const CONSIGNMENT_WITHDRAW_KEY = ['outbox', 'consignments', 'withdraw'] as const

queryClient.setMutationDefaults(CONSIGNMENT_CREATE_KEY, { mutationFn: createConsignment })
queryClient.setMutationDefaults(CONSIGNMENT_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: ConsignmentTermsBody }) =>
    updateConsignment(vars.id, vars.body),
})
queryClient.setMutationDefaults(CONSIGNMENT_DELETE_KEY, { mutationFn: deleteConsignment })
queryClient.setMutationDefaults(CONSIGNMENT_SALE_KEY, {
  mutationFn: (vars: { id: string; body: RecordSaleBody }) => recordSale(vars.id, vars.body),
})
queryClient.setMutationDefaults(CONSIGNMENT_PAYOUT_KEY, {
  mutationFn: (vars: { id: string; paidOutAt?: string }) =>
    recordPayout(vars.id, vars.paidOutAt),
})
queryClient.setMutationDefaults(CONSIGNMENT_WITHDRAW_KEY, { mutationFn: withdrawConsignment })

export const useCreateConsignment = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  const mutation = useMutation<Consignment, Error, ConsignmentWriteBody & { id: string }>({
    mutationKey: CONSIGNMENT_CREATE_KEY,
    mutationFn: createConsignment,
    onSuccess: invalidate,
  })
  return {
    ...mutation,
    mutate: (body: ConsignmentWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: ConsignmentWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  }
}

export const useUpdateConsignment = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  return useMutation({
    mutationKey: CONSIGNMENT_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: ConsignmentTermsBody }) =>
      updateConsignment(vars.id, vars.body),
    onSuccess: invalidate,
  })
}

export const useDeleteConsignment = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  return useMutation({
    mutationKey: CONSIGNMENT_DELETE_KEY,
    mutationFn: (id: string) => deleteConsignment(id),
    onSuccess: invalidate,
  })
}

export const useRecordSale = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  return useMutation({
    mutationKey: CONSIGNMENT_SALE_KEY,
    mutationFn: (vars: { id: string; body: RecordSaleBody }) => recordSale(vars.id, vars.body),
    onSuccess: invalidate,
  })
}

export const useRecordPayout = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  return useMutation({
    mutationKey: CONSIGNMENT_PAYOUT_KEY,
    mutationFn: (vars: { id: string; paidOutAt?: string }) => recordPayout(vars.id, vars.paidOutAt),
    onSuccess: invalidate,
  })
}

export const useWithdrawConsignment = () => {
  const invalidate = useInvalidateConsignmentsAndBoats()
  return useMutation({
    mutationKey: CONSIGNMENT_WITHDRAW_KEY,
    mutationFn: (id: string) => withdrawConsignment(id),
    onSuccess: invalidate,
  })
}
